import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import useRecents from '../features/nav/useRecents';
import useTheme, { THEME_MODE_LABEL } from '../lib/useTheme';
import { searchPages, isBodySearchMissing, renderSnippet } from '../pages/SearchPage';
import './CommandPalette.css';

const TYPE_EMOJI = { document: '📄', database: '🗂️', db_row: '📄' };
const RECENT_SHOW = 8;

/**
 * Cmd+K / Ctrl+K 커맨드 팔레트.
 * - 입력 시 페이지 검색 (RPC 전문검색 → 제목 ilike 폴백, debounce 250ms)
 * - 빈 입력: 액션 항목 + 최근 방문 페이지
 * - ↑↓/Enter 키보드 네비, ESC/오버레이 클릭 닫기
 */
export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { user, membership } = useAuth();
  const { recentIds } = useRecents();
  const { mode: themeMode, cycleMode: cycleTheme } = useTheme();

  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);       // 검색 결과 페이지
  const [recentPages, setRecentPages] = useState([]); // 최근 방문 페이지 상세
  const [loading, setLoading] = useState(false);
  const [ftsMissing, setFtsMissing] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // 열릴 때 상태 초기화 + 입력 포커스
  useEffect(() => {
    if (!open) return;
    setQ('');
    setResults([]);
    setActiveIndex(0);
    // autoFocus 가 재오픈 시 동작하지 않는 경우 대비
    const t = setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
    return () => clearTimeout(t);
  }, [open]);

  // 최근 방문 페이지 상세 로드 (열릴 때 1회)
  useEffect(() => {
    if (!open || !user || recentIds.length === 0) { setRecentPages([]); return undefined; }
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .schema('workspace')
          .from('pages')
          .select('id, title, icon, type')
          .in('id', recentIds)
          .eq('archived', false);
        if (!active) return;
        if (error) { setRecentPages([]); return; }
        const byId = new Map((data || []).map((p) => [p.id, p]));
        setRecentPages(recentIds.map((id) => byId.get(id)).filter(Boolean).slice(0, RECENT_SHOW));
      } catch (e) {
        if (active) setRecentPages([]);
      }
    })();
    return () => { active = false; };
  }, [open, user, recentIds]);

  // 검색 (debounce 250ms)
  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (!term) { setResults([]); setLoading(false); return undefined; }
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      const { rows } = await searchPages(membership?.org_id, term);
      if (active) {
        setResults(rows);
        setFtsMissing(isBodySearchMissing());
        setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [open, q, membership?.org_id]);

  const close = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const go = useCallback((path) => {
    close();
    navigate(path);
  }, [close, navigate]);

  // 액션: 새 페이지 만들기 (생성 후 이동 + 사이드바 갱신 신호)
  const createNewPage = useCallback(async () => {
    if (!membership?.org_id || !user?.id) return;
    try {
      const { data, error } = await supabase
        .schema('workspace')
        .from('pages')
        .insert({
          org_id: membership.org_id,
          type: 'document',
          title: '',
          position: Date.now(),
          created_by: user.id,
        })
        .select('id')
        .single();
      if (error || !data) return;
      try { window.dispatchEvent(new Event('rt:pages')); } catch (e) { /* noop */ }
      go(`/page/${data.id}`);
    } catch (e) { /* noop */ }
  }, [membership?.org_id, user?.id, go]);

  const actions = [
    { key: 'act-new', icon: '＋', label: '새 페이지 만들기', run: createNewPage },
    { key: 'act-search', icon: '🔍', label: '검색 페이지 열기', run: () => go('/search') },
    { key: 'act-inbox', icon: '📥', label: '수신함', run: () => go('/inbox') },
    { key: 'act-trash', icon: '🗑️', label: '휴지통', run: () => go('/trash') },
    {
      key: 'act-theme',
      icon: '🌓',
      // 라이트 → 다크 → 시스템 순환. 팔레트는 닫지 않아 연속 전환 가능(라벨이 현재 모드 표시).
      label: `다크모드 전환 (현재: ${THEME_MODE_LABEL[themeMode] || '시스템'})`,
      run: cycleTheme,
    },
  ];

  // 표시 항목 구성 — 검색어 있으면: 결과 + 라벨 매칭 액션 / 없으면: 액션 + 최근 방문
  const term = q.trim();
  const items = [];
  if (term) {
    results.forEach((p) => items.push({
      key: `pg-${p.id}`,
      kind: 'page',
      icon: p.icon || TYPE_EMOJI[p.type] || '📄',
      label: p.title || '제목 없음',
      snippet: p.snippet || null,
      run: () => go(`/page/${p.id}`),
    }));
    actions
      .filter((a) => a.label.toLowerCase().includes(term.toLowerCase()))
      .forEach((a) => items.push({ ...a, kind: 'action' }));
  } else {
    actions.forEach((a) => items.push({ ...a, kind: 'action' }));
    recentPages.forEach((p) => items.push({
      key: `rc-${p.id}`,
      kind: 'recent',
      icon: p.icon || TYPE_EMOJI[p.type] || '📄',
      label: p.title || '제목 없음',
      snippet: null,
      run: () => go(`/page/${p.id}`),
    }));
  }

  // 항목 수 변화 시 activeIndex 보정
  useEffect(() => {
    setActiveIndex((i) => (items.length === 0 ? 0 : Math.min(i, items.length - 1)));
    // eslint-disable-next-line
  }, [q, results, recentPages, open]);

  // 활성 항목 스크롤 노출
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function onKeyDown(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (items.length === 0 ? 0 : (i + 1) % items.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (items.length === 0 ? 0 : (i - 1 + items.length) % items.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[activeIndex];
      if (item) item.run();
    }
  }

  if (!open || !user) return null;

  // 섹션 라벨 (빈 입력일 때만)
  const firstRecentIdx = items.findIndex((it) => it.kind === 'recent');

  return (
    <div
      className="cmdk-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="cmdk-panel" role="dialog" aria-modal="true" aria-label="커맨드 팔레트" onKeyDown={onKeyDown}>
        <div className="cmdk-inputwrap">
          <span className="cmdk-inputicon">🔍</span>
          <input
            ref={inputRef}
            className="cmdk-input"
            autoFocus
            placeholder="검색 또는 명령 입력…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <kbd className="cmdk-kbd">ESC</kbd>
        </div>
        <div className="cmdk-list" ref={listRef}>
          {!term && items.length > 0 && <div className="cmdk-seclabel">액션</div>}
          {items.map((item, idx) => (
            <div key={item.key}>
              {idx === firstRecentIdx && <div className="cmdk-seclabel">최근 방문</div>}
              <div
                data-idx={idx}
                className={`cmdk-item${idx === activeIndex ? ' active' : ''}`}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => {
                  item.run();
                  // 팔레트가 열린 채 남는 액션(테마 전환 등) 후에도 ↑↓/Enter 키보드
                  // 네비가 계속 동작하도록 입력으로 재포커스 (닫힌 경우엔 무해)
                  if (inputRef.current) inputRef.current.focus();
                }}
              >
                <span className="cmdk-ic">{item.icon}</span>
                <span className="cmdk-body">
                  <span className="cmdk-label">{item.label}</span>
                  {item.snippet && <span className="cmdk-snippet">{renderSnippet(item.snippet)}</span>}
                </span>
                {idx === activeIndex && <kbd className="cmdk-kbd">↵</kbd>}
              </div>
            </div>
          ))}
          {term && loading && <div className="cmdk-empty">검색 중…</div>}
          {term && !loading && items.length === 0 && <div className="cmdk-empty">결과가 없습니다.</div>}
          {!term && items.length === 0 && <div className="cmdk-empty">최근 방문한 페이지가 없습니다.</div>}
        </div>
        <div className="cmdk-foot">
          <span><kbd className="cmdk-kbd">↑</kbd><kbd className="cmdk-kbd">↓</kbd> 이동</span>
          <span><kbd className="cmdk-kbd">↵</kbd> 열기</span>
          {term && ftsMissing && (
            <span className="cmdk-footnote">본문 검색은 마이그레이션 0021 적용 후 사용 가능</span>
          )}
        </div>
      </div>
    </div>
  );
}
